import { defineStore } from 'pinia';
import { UserDTO } from 'src/types';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import { usePropertyStore } from 'src/stores/propertyStore';
import {
  AuthenticateUserRequestDTO,
  BadRequestResponseDTO,
  RegisterUserRequestDTO,
} from 'src/services/api/data-contracts';
import { Notify } from 'quasar';
import { AxiosError } from 'axios';
import useValidationUtils from 'src/composables/validationUtils';
import { i18n } from 'boot/i18n';
const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    user: null as UserDTO,
    isRegisteringUser: false as boolean,
    isLoggingIn: false as boolean,
    showLoginDialog: false as boolean,
  }),
  getters: {
    isUserLoggedIn(): boolean {
      return this.user != null;
    },
  },
  actions: {
    // Load the user if they are logged in.
    getUser(propertyId: number) {
      const accountPromise = apiService.accountGet({
        propertyId: propertyId,
      });
      accountPromise.then((accountResponse) => {
        if (accountResponse.data != null) {
          // could be logged out.
          const user = new UserDTO();
          user.firstName = accountResponse.data.Firstname;
          user.lastName = accountResponse.data.Lastname;
          user.telephoneNumber = accountResponse.data.Telephone;
          user.emailAddress = accountResponse.data.EmailAddress;
          user.receiveBrandOffers = accountResponse.data.ReceiveBrandOffers;
          user.canChoosePaymentMethod =
            accountResponse.data.CanChoosePaymentMethod;
          user.creditCards = accountResponse.data.CreditCards;

          this.user = user;
        }
      });
    },
    async registerUser(
      emailAddress: string,
      password: string,
      firstName: string,
      lastName: string,
      telephoneNumber: string,
    ) {
      this.isRegisteringUser = true;

      const ps = usePropertyStore();

      const model = {
        LanguageCode: 'en',
        PropertyId: ps.propertyId,
        EmailAddress: emailAddress,
        Password: password,
        Firstname: firstName,
        Lastname: lastName,
        MobileNumber: telephoneNumber,
      } as RegisterUserRequestDTO;

      const registerPromise = apiService.accountRegister(model);

      registerPromise.then((registerResponse) => {
        if (registerResponse.data != null) {
          const user = new UserDTO();
          user.firstName = registerResponse.data.Firstname;
          user.lastName = registerResponse.data.Lastname;
          user.telephoneNumber = registerResponse.data.Telephone;
          user.emailAddress = registerResponse.data.EmailAddress;

          this.user = user;

          Notify.create({
            color: 'positive',
            position: 'top',
            message: t('user.registerSuccessMessage'),
          });
        }
      });
      registerPromise.catch((errorResponse: AxiosError) => {
        if (errorResponse.response?.data != null) {
          const badResponse = errorResponse.response
            ?.data as BadRequestResponseDTO;
          if (badResponse != null) {
            //badResponse.ModelState
          }
          Notify.create({
            icon: 'error',
            color: 'negative',
            position: 'top',
            message: t('user.registerErrorMessage'),
          });
        }
      });

      this.isRegisteringUser = false;

      return registerPromise;
    },
    login(emailAddress: string, password: string) {
      this.isLoggingIn = true;

      // TODO JC get the propertyId or propertyCode from the url here.
      const ps = usePropertyStore();
      const validationUtils = useValidationUtils();

      const loginPromise = apiService.accountAuthenticate({
        LanguageCode: 'en',
        PropertyId: ps.propertyId,
        EmailAddress: emailAddress,
        Password: password,
      } as AuthenticateUserRequestDTO);

      loginPromise.then((loginResponse) => {
        if (loginResponse.data != null) {
          if (loginResponse.data.Success) {
            const user = new UserDTO();
            user.firstName = loginResponse.data.Firstname;
            user.lastName = loginResponse.data.Lastname;
            user.telephoneNumber = loginResponse.data.Telephone;
            user.emailAddress = loginResponse.data.EmailAddress;
            user.receiveBrandOffers = loginResponse.data.ReceiveBrandOffers;
            user.canChoosePaymentMethod =
              loginResponse.data.CanChoosePaymentMethod;
            user.creditCards = loginResponse.data.CreditCards;

            this.user = user;

            this.showLoginDialog = false;

            Notify.create({
              color: 'positive',
              position: 'top',
              message: t('user.loginSuccessMessage', { name: user.firstName }),
            });
          } else {
            Notify.create({
              icon: 'error',
              color: 'negative',
              position: 'top',
              message: loginResponse.data.Message,
            });
          }
        }
      });
      loginPromise.catch((errorResponse: AxiosError) => {
        if (errorResponse.response?.data != null) {
          const badResponse = errorResponse.response
            ?.data as BadRequestResponseDTO;
          const errorMessage = validationUtils.getErrorMessage(badResponse);

          Notify.create({
            icon: 'error',
            color: 'negative',
            position: 'top',
            message: errorMessage ?? t('user.loginErrorMessage'),
          });
        }
      });
      loginPromise.finally(() => {
        this.isLoggingIn = false;
      });
    },
    logout() {
      if (this.isUserLoggedIn) {
        const logoutPromise = apiService.accountLogout();
        logoutPromise.then(() => {
          Notify.create({
            color: 'positive',
            position: 'top',
            message: t('user.logoutSuccessMessage'),
          });
        });
        logoutPromise.finally(() => {
          this.user = null;
        });
      }
    },
  },
});
